"use client";

import React, { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import "./PeatSenseFAQ.scss";
import Footer from "@/components/footer/footer";

interface FAQItem {
  id: number;
  question: string;
  answer: string;
  category: "general" | "platform" | "technical" | "pricing" | string;
}

export default function AI2PeatFAQ() {
  const [allFaqs, setAllFaqs] = useState<FAQItem[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Load from your Next proxy → Django (/api/faq → /faq/)
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await fetch("/api/faq", { cache: "no-store" });
        if (!res.ok) {
          throw new Error(`Failed to load FAQs (${res.status})`);
        }

        const json = await res.json();
        const items: FAQItem[] = Array.isArray(json)
          ? json
          : Array.isArray(json?.results)
          ? json.results
          : [];

        if (!mounted) return;
        setAllFaqs(items);
        setExpandedId(items.length ? items[0].id : null);
      } catch (e: any) {
        if (!mounted) return;
        setError(e?.message ?? "Failed to load FAQs");
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  // Apply category filter first
  const categoryFiltered = useMemo(() => {
    if (activeFilter === "all") return allFaqs;
    return allFaqs.filter((f) => f.category === activeFilter);
  }, [allFaqs, activeFilter]);

  // Then apply search
  const displayFAQs = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return categoryFiltered;
    return categoryFiltered.filter(
      (f) =>
        f.question.toLowerCase().includes(q) ||
        f.answer.toLowerCase().includes(q)
    );
  }, [categoryFiltered, searchQuery]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchQuery(val);
    if (val.trim() && displayFAQs.length > 0) {
      setExpandedId(displayFAQs[0].id);
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  const toggleFAQ = (id: number) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const setFilter = (category: string) => {
    setActiveFilter(category);
    const next = category === "all" ? allFaqs : allFaqs.filter((f) => f.category === category);
    setExpandedId(next.length ? next[0].id : null);
  };

  return (
    <>
     <div className="ai2peat-faq">
      <div className="faq-container">
        <header className="faq-header">
          <h1>Frequently asked questions</h1>
          <p className="subtitle">
            These are the most commonly asked questions about AI2Peat.<br />
            {"Can't find what you're looking for? "}
            <Link href="/contact-us">
              Chat to our friendly team!
            </Link>
          </p>

          <div className="category-tabs">
            <button
              className={`tab-btn ${activeFilter === "all" ? "active" : ""}`}
              onClick={() => setFilter("all")}
            >
              All
            </button>
            <button
              className={`tab-btn ${activeFilter === "general" ? "active" : ""}`}
              onClick={() => setFilter("general")}
            >
              General
            </button>
            <button
              className={`tab-btn ${activeFilter === "platform" ? "active" : ""}`}
              onClick={() => setFilter("platform")}
            >
              Platform
            </button>
            <button
              className={`tab-btn ${activeFilter === "technical" ? "active" : ""}`}
              onClick={() => setFilter("technical")}
            >
              Technical
            </button>
            <button
              className={`tab-btn ${activeFilter === "pricing" ? "active" : ""}`}
              onClick={() => setFilter("pricing")}
            >
              Pricing
            </button>
          </div>

          <div className="search-container">
            <div className="search-box">
              <svg className="search-icon" width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M12.5 11H11.71L11.43 10.73C12.41 9.59 13 8.11 13 6.5C13 2.91 10.09 0 6.5 0C2.91 0 0 2.91 0 6.5C0 10.09 2.91 13 6.5 13C8.11 13 9.59 12.41 10.73 11.43L11 11.71V12.5L16 17.49L17.49 16L12.5 11ZM6.5 11C4.01 11 2 8.99 2 6.5C2 4.01 4.01 2 6.5 2C8.99 2 11 4.01 11 6.5C11 8.99 8.99 11 6.5 11Z" fill="#666666" />
              </svg>
              <input
                type="text"
                placeholder="Search questions..."
                value={searchQuery}
                onChange={handleSearch}
                className="search-input"
              />
              {searchQuery && (
                <button className="clear-search" onClick={clearSearch}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z" fill="#666666" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </header>

        {loading ? (
          <div className="no-results">
            <h3>Loading FAQs…</h3>
          </div>
        ) : error ? (
          <div className="no-results">
            <h3>Unable to load FAQs</h3>
            <p>{error}</p>
          </div>
        ) : (
          <div className="faq-list">
            {displayFAQs.length > 0 ? (
              displayFAQs.map((faq) => (
                <div
                  key={faq.id}
                  className={`faq-item ${expandedId === faq.id ? "expanded" : ""}`}
                >
                  <div className="faq-question" onClick={() => toggleFAQ(faq.id)}>
                    <div className="question-text">
                      <span className="question-marker">•</span>
                      <h3>{faq.question}</h3>
                    </div>
                    <div className="toggle-icon">
                      {expandedId === faq.id ? (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M19 13H5C4.45 13 4 12.55 4 12C4 11.45 4.45 11 5 11H19C19.55 11 20 11.45 20 12C20 12.55 19.55 13 19 13Z" fill="#000000" />
                        </svg>
                      ) : (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M19 13H13V19C13 19.55 12.55 20 12 20C11.45 20 11 19.55 11 19V13H5C4.45 13 4 12.55 4 12C4 11.45 4.45 11 5 11H11V5C11 4.45 11.45 4 12 4C12.55 4 13 4.45 13 5V11H19C19.55 11 20 11.45 20 12C20 12.55 19.55 13 19 13Z" fill="#000000" />
                        </svg>
                      )}
                    </div>
                  </div>

                  {expandedId === faq.id && (
                    <div className="faq-answer">
                      <p>{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="no-results">
                <svg className="search-icon-large" width="48" height="48" viewBox="0 0 48 48" fill="none">
                  <path d="M31 28H29.21L28.93 27.73C30.41 25.89 31.5 23.41 31.5 20.5C31.5 14.42 26.58 9.5 20.5 9.5C14.42 9.5 9.5 14.42 9.5 20.5C9.5 26.58 14.42 31.5 20.5 31.5C23.41 31.5 25.89 30.41 27.73 28.93L28 29.21V31L37 40L40 37L31 28ZM20.5 28C16.91 28 14 25.09 14 20.5C14 15.91 16.91 13 20.5 13C24.09 13 27 15.91 27 20.5C27 24.09 24.09 28 20.5 28Z" fill="#CCCCCC" />
                </svg>
                <h3>No results found</h3>
                {searchQuery ? (
                  <p>{`We couldn't find any FAQs matching "${searchQuery}"`}</p>
                ) : (
                  <p>No FAQs have been published yet.</p>
                )}
                {searchQuery && (
                  <button className="clear-search-btn" onClick={clearSearch}>
                    Clear search
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        <footer className="faq-footer">
          <div className="footer-content">
            <p className="footer-text">
              Still have questions?{" "}
              <Link href="/contact-us">
                Contact our team
              </Link>{" "}
              for more information.
            </p>
            <div className="footer-brand">
              <span className="brand-name">AI2Peat</span>
              <span className="brand-tagline">Monitoring peatlands for a sustainable future</span>
            </div>
          </div>
        </footer>
      </div>
      <Footer />
    </div>
 
    </>
   
  );
}